import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Search, Users, Building2, User, RefreshCw, Phone, Mail, MapPin, Edit, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { validarRutChileno, consultarSII } from '../lib/rut';

export default function ContactosPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  // Iter 235.l (Carlos): edición bidireccional ERP ↔ Alegra
  const [editContact, setEditContact] = useState(null); // contacto en edición o null
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const openEdit = (c) => {
    setEditContact(c);
    setEditForm({
      name: c.nombre || '',
      identification: c.identificacion || '',
      email: c.email || '',
      phonePrimary: c.telefono || '',
      address: c.direccion || '',
      status: (c.datos_completos?.status || 'active'),
    });
  };

  const closeEdit = () => {
    setEditContact(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editContact) return;
    // Iter 292.a (Carlos) — Bloquear guardado si el RUT no cumple módulo 11.
    const rutTrim = (editForm.identification || '').trim();
    if (rutTrim && !validarRutChileno(rutTrim)) {
      toast.error('Error en RUT. Por favor, revíselo y vuelva a ingresarlo.');
      return;
    }
    setEditSaving(true);
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => {
        if (v !== undefined && v !== null) payload[k] = v;
      });
      const res = await api.put(`/alegra/contacts/${editContact.alegra_id}`, payload);
      toast.success(`Contacto ${res.data.contact?.nombre || ''} actualizado en Alegra + ERP`);
      closeEdit();
      await loadContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar contacto');
    } finally {
      setEditSaving(false);
    }
  };

  const syncFromAlegra = async (alegra_id) => {
    setSyncingId(alegra_id);
    try {
      const res = await api.post(`/alegra/contacts/${alegra_id}/sync-from-alegra`);
      toast.success(`Contacto ${res.data.contact?.nombre || alegra_id} re-sincronizado desde Alegra`);
      await loadContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al sincronizar');
    } finally {
      setSyncingId(null);
    }
  };

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadContacts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, tipoFilter, debouncedSearch]);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: ((page - 1) * limit).toString(),
        limit: limit.toString()
      });
      if (tipoFilter) params.append('tipo', tipoFilter);
      if (debouncedSearch.trim()) params.append('search', debouncedSearch.trim());
      
      const res = await api.get(`/alegra/contacts?${params}`);
      setContacts(res.data.contacts || []);
      setTotal(res.data.total || 0);
    } catch (error) {

      toast.error('Error al cargar contactos');
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = contacts;

  const getTipoIcon = (tipo) => {
    if (!tipo) return <User className="h-4 w-4 text-gray-400" />;
    const tipoStr = Array.isArray(tipo) ? tipo[0] : tipo;
    if (tipoStr === 'client') return <User className="h-4 w-4 text-blue-500" />;
    if (tipoStr === 'provider') return <Building2 className="h-4 w-4 text-green-500" />;
    return <Users className="h-4 w-4 text-gray-400" />;
  };

  const getTipoLabel = (tipo) => {
    if (!tipo) return '-';
    const tipoStr = Array.isArray(tipo) ? tipo.join(', ') : tipo;
    return tipoStr === 'client' ? 'Cliente' : tipoStr === 'provider' ? 'Proveedor' : tipoStr;
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && contacts.length === 0) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="contactos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold">Contactos</h1>
          <p className="text-sm text-muted-foreground">{total} contactos de Alegra</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadContacts} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, RUT, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-9"
              />
            </div>
            <select
              value={tipoFilter}
              onChange={(e) => { setTipoFilter(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm w-full sm:w-[160px]"
            >
              <option value="">Todos los tipos</option>
              <option value="client">Clientes</option>
              <option value="provider">Proveedores</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredContacts.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No se encontraron contactos</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="text-xs">
                    <TableHead className="w-10">Tipo</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>RUT</TableHead>
                    <TableHead className="hidden md:table-cell">Email</TableHead>
                    <TableHead className="hidden lg:table-cell">Teléfono</TableHead>
                    <TableHead className="hidden xl:table-cell">Dirección</TableHead>
                    <TableHead className="w-[100px] text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContacts.map((contact) => (
                    <TableRow key={contact.alegra_id} className={`text-sm ${contact.datos_completos?.status === 'inactive' ? 'opacity-60' : ''}`} data-testid={`contact-row-${contact.alegra_id}`}>
                      <TableCell className="py-2">
                        <div className="flex items-center justify-center" title={getTipoLabel(contact.tipo)}>
                          {getTipoIcon(contact.tipo)}
                        </div>
                      </TableCell>
                      <TableCell className="py-2 font-medium max-w-[200px] truncate">
                        {contact.nombre}
                        {contact.datos_completos?.status === 'inactive' && (
                          <span className="ml-2 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">inactivo</span>
                        )}
                      </TableCell>
                      <TableCell className="py-2 font-mono text-xs">
                        {contact.identificacion || '-'}
                      </TableCell>
                      <TableCell className="py-2 hidden md:table-cell text-xs">
                        {contact.email ? (
                          <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </a>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="py-2 hidden lg:table-cell text-xs">
                        {contact.telefono ? (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contact.telefono}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="py-2 hidden xl:table-cell text-xs max-w-[200px] truncate">
                        {contact.direccion ? (
                          <span className="flex items-center gap-1" title={contact.direccion}>
                            <MapPin className="h-3 w-3" />
                            {contact.direccion}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => openEdit(contact)}
                            className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-blue-50 text-blue-600"
                            title="Editar contacto (actualiza Alegra + ERP)"
                            data-testid={`edit-contact-${contact.alegra_id}`}
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => syncFromAlegra(contact.alegra_id)}
                            disabled={syncingId === contact.alegra_id}
                            className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-emerald-50 text-emerald-600 disabled:opacity-40"
                            title="Re-sincronizar desde Alegra (forzar refresh)"
                            data-testid={`sync-contact-${contact.alegra_id}`}
                          >
                            <RefreshCw className={`w-3.5 h-3.5 ${syncingId === contact.alegra_id ? 'animate-spin' : ''}`} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Página {page} de {totalPages} ({total} contactos)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
      {/* Edit modal — Iter 235.l (Carlos): edición bidireccional Alegra primero */}
      <Dialog open={!!editContact} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent className="max-w-md" data-testid="contact-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar contacto</DialogTitle>
            <p className="text-xs text-muted-foreground">
              Los cambios se aplicarán primero en <b>Alegra</b> y luego se refrescará el cache del ERP.
            </p>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nombre</Label>
              <Input value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-name" />
            </div>
            <div>
              <Label className="text-xs">Identificación (RUT)</Label>
              <Input
                value={editForm.identification || ''}
                onChange={(e) => setEditForm({ ...editForm, identification: e.target.value })}
                className={editForm.identification && editForm.identification.trim() && !validarRutChileno(editForm.identification) ? 'border-red-500 focus-visible:ring-red-500' : ''}
                aria-invalid={!!(editForm.identification && editForm.identification.trim() && !validarRutChileno(editForm.identification))}
                data-testid="edit-identification"
              />
              {editForm.identification && editForm.identification.trim() && !validarRutChileno(editForm.identification) && (
                <p className="text-xs text-red-600 mt-1" data-testid="edit-identification-error">
                  Error en RUT. Por favor, revíselo y vuelva a ingresarlo.
                </p>
              )}
              <button
                type="button"
                onClick={() => consultarSII(editForm.identification, toast)}
                className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1"
                data-testid="edit-consultar-sii"
              >
                <ExternalLink className="w-3 h-3" /> Consultar situación tributaria en SII
              </button>
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-email" />
            </div>
            <div>
              <Label className="text-xs">Teléfono</Label>
              <Input value={editForm.phonePrimary || ''} onChange={(e) => setEditForm({ ...editForm, phonePrimary: e.target.value })} data-testid="edit-phone" />
            </div>
            <div>
              <Label className="text-xs">Dirección</Label>
              <Input value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} data-testid="edit-address" />
            </div>
            <div>
              <Label className="text-xs">Estado</Label>
              <select
                value={editForm.status || 'active'}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm w-full"
                data-testid="edit-status"
              >
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>
            {editContact && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                ID Alegra: <span className="font-mono">{editContact.alegra_id}</span>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEdit} disabled={editSaving}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={editSaving || !!(editForm.identification && editForm.identification.trim() && !validarRutChileno(editForm.identification))} data-testid="edit-save-btn">
              {editSaving ? 'Guardando…' : 'Guardar en Alegra + ERP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
